import React from 'react';
import { Button, Container, Grid, useMediaQuery, Link, Box, Theme } from '@mui/material';
import { CoreGlobalFooter } from '@care/navigation';
import { Divider } from '@care/react-component-lib';
import getConfig from 'next/config';
import Profile from '@/components/Profile';
import Verifications from '@/components/Verifications';
import ProviderIntro from '@/components/ProviderIntro';
import CanAlsoHelpWith from '@/components/CanAlsoHelpWith';
import RecentReview from '@/components/RecentReview';
import CaregiverEducation from '@/components/CaregiverEducation';
import SimilarProfiles from '@/components/SimilarProfiles';
import CareOptions from '@/components/CareOptions';
import SeoConversionBox from '@/components/SeoConversionBox';
import Breadcrumbs from '@/components/Breadcrumbs';
import NavBar from '@/components/SeoGlobalNavbar';
import Highlights from '@/components/Highlights';
import CaregiverZipCodeCTA from '@/components/CaregiverZipCodeCTA';
import generateEnrollmentUrl from '@/utils/generateEnrollmentUrl';
import getProviderMetadata from '@/utils/getProviderMetadata';
import mapQualitiesAndServices from '@/utils/mapQualitiesAndServices';
import { SEO_SERVICE_MAP_SHORT_NAMES } from '@/constants';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { type GetProviderDetailsDataProps } from '@/components/pages/segmentationDetails/caregiver/caregiverDetailPageProps';
import { ProfilePageSchema } from '@/components/CaregiverProfile/ProfilePageSchema';
import useAmplitudeTracking from './hooks/useAmplitudeTracking';
import useMetaTags from './hooks/useMetaTags';
import MetaHead from './shared/MetaHead';

const {
  publicRuntimeConfig: { NEXT_PUBLIC_APP_URL },
} = getConfig();

const CaregiverDetail = ({
  pageData: data,
  serviceShortName,
  testName,
  deviceChar,
  seoSegmentId,
}: GetProviderDetailsDataProps) => {
  const hasCareCheck = data?.getSEOSegmentationCaregiverDetails.caregiver.hasCareCheck;
  const hasAiBio =
    data?.getSEOSegmentationCaregiverDetails.caregiver.profiles?.commonCaregiverProfile.bio
      ?.aiAssistedBio ?? false;

  useAmplitudeTracking<GetProviderDetailsDataProps>({
    deviceChar,
    testName,
    hasCareCheck,
    serviceShortName,
    hasAiBio,
  });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const { caregiver, latestCaregiverReview, nearByCaregivers } =
    data?.getSEOSegmentationCaregiverDetails || {};
  const memberInfo = caregiver?.member;
  const breadcrumbs = data?.getSEOSegmentationBreadcrumbs.breadcrumbs || [];
  const caregiverProfile = caregiver.profiles?.commonCaregiverProfile;
  const seoProfileUrl = NEXT_PUBLIC_APP_URL + seoSegmentId;
  const providerMetadata = getProviderMetadata({
    displayName: memberInfo?.displayName,
    firstName: memberInfo.firstName,
    address: {
      city: memberInfo?.address.city,
      state: memberInfo?.address.state,
    },
    yearsOfExperience: caregiver?.yearsOfExperience,
    availability: caregiverProfile?.availabilityFrequency,
    // render Care's logo if provider doesn't have profile picture
    image: memberInfo?.hiResImageURL
      ? memberInfo.hiResImageURL
      : `${NEXT_PUBLIC_APP_URL}/img/care-no-tag.jpg`,
    serviceName: SEO_SERVICE_MAP_SHORT_NAMES[serviceShortName],
    hourlyRate: caregiverProfile?.payRange?.hourlyRateFrom.amount,
    bio: caregiverProfile?.bio?.experienceSummary || null,
    totalReviews: caregiver.numberOfReviews ?? 0,
    isPremium: caregiverProfile.isPremium,
  });
  const caregiverQualities = caregiverProfile
    ? mapQualitiesAndServices(caregiverProfile.qualities)
    : [];
  const caregiverServices = caregiverProfile
    ? mapQualitiesAndServices(caregiverProfile.supportedServices)
    : [];
  const enrollmentURL = generateEnrollmentUrl({
    serviceShortName,
    serviceIdForMember: memberInfo?.subService,
  });
  const handleJoinFreeToSeeProfile = (e: React.MouseEvent<HTMLButtonElement>) => {
    const amplitudeData = {
      cta_text: (e.target as HTMLElement).textContent as string,
      cta_type: 'button',
    };
    trackAmplitudeEvent(AmplitudeEvent.ctaInteracted, amplitudeData);
  };
  const metaTags = useMetaTags(providerMetadata);
  return (
    <>
      <MetaHead
        pageTitle={providerMetadata.title}
        metaTags={metaTags}
        seoProfileUrl={seoProfileUrl}
      />
      <ProfilePageSchema
        displayName={memberInfo.displayName}
        description={providerMetadata.description}
        imageURL={providerMetadata.image}
      />
      <NavBar enrollmentURL={enrollmentURL} />
      <Grid container mb={8}>
        {!isMobile && (
          <Grid item xs={12}>
            <Container maxWidth="md">
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </Container>
          </Grid>
        )}
        <Grid item xs={12}>
          <Container maxWidth="md" disableGutters>
            <CaregiverZipCodeCTA
              enrollmentURL={enrollmentURL}
              serviceShortName={serviceShortName}
            />
          </Container>
        </Grid>
        <Grid item xs={12}>
          <Profile
            displayName={memberInfo.displayName}
            firstName={memberInfo.firstName}
            address={memberInfo.address}
            hiResImageURL={memberInfo.hiResImageURL}
            joinNowURL={`${enrollmentURL}&visitedServiceProfileId=${caregiverProfile.id}`}
            hasCareCheck={caregiver.hasCareCheck}
            numberOfReviews={caregiver.numberOfReviews}
            yearsOfExperience={caregiver.yearsOfExperience}
            avgReviewRating={caregiver.avgReviewRating}
            payRange={caregiverProfile.payRange}
            availability={caregiverProfile.availabilityFrequency}
          />
        </Grid>
        {!isMobile && (
          <Grid item xs={12}>
            <SeoConversionBox header="When do you need care?" enrollmentURL={enrollmentURL} />
          </Grid>
        )}
        {isMobile && (
          <Grid item xs={12}>
            <Divider sx={{ py: 1 }} />
          </Grid>
        )}
        <Grid item xs={12}>
          <ProviderIntro
            memberDetail={caregiver.member}
            profileDesc={caregiverProfile.bio.experienceSummary}
          />
        </Grid>
        {caregiverQualities?.length > 0 && (
          <Grid item xs={12}>
            <Highlights qualities={caregiverQualities} />
          </Grid>
        )}
        {caregiverServices?.length > 0 && (
          <>
            <Container maxWidth="md" disableGutters>
              <Divider sx={{ py: 1 }} />
            </Container>
            <Grid item xs={12}>
              <CanAlsoHelpWith firstName={memberInfo.firstName} sideJobsList={caregiverServices} />
            </Grid>
          </>
        )}
        {latestCaregiverReview && (
          <>
            <Container maxWidth="md" disableGutters>
              <Divider sx={{ py: 1 }} />
            </Container>
            <Grid item xs={12} id="recentReview">
              <RecentReview
                latestReview={latestCaregiverReview}
                numOfReviews={caregiver.numberOfReviews}
                joinNowURL={enrollmentURL}
              />
            </Grid>
          </>
        )}
        {caregiver.collegeDegrees?.length > 0 && (
          <>
            <Container maxWidth="md" disableGutters>
              <Divider sx={{ py: 1 }} />
            </Container>
            <CaregiverEducation collegeDegrees={caregiver.collegeDegrees} />
          </>
        )}
        {caregiver.verifications && (
          <>
            <Container maxWidth="md" disableGutters>
              <Divider sx={{ py: 1 }} />
            </Container>
            <Grid item xs={12}>
              <Verifications features={caregiver.verifications} title="Verified information" />
            </Grid>
          </>
        )}
        <Grid item xs={12}>
          <Box m={3}>
            <Box textAlign="center">
              <Link href={enrollmentURL} rel="nofollow">
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  onClick={handleJoinFreeToSeeProfile}
                  sx={{ width: '324px' }}>
                  {caregiver.hasCareCheck ? 'Join free to see full profile' : 'Join now'}
                </Button>
              </Link>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {nearByCaregivers?.length > 0 && <SimilarProfiles profiles={nearByCaregivers} />}
        </Grid>
        <Grid item xs={12}>
          <CareOptions hasCareCheck={caregiver.hasCareCheck} />
        </Grid>
        {isMobile && (
          <>
            <Grid item xs={12}>
              <SeoConversionBox header="When do you need care?" enrollmentURL={enrollmentURL} />
            </Grid>
            <Grid item xs={12}>
              <Container>
                <Breadcrumbs breadcrumbs={breadcrumbs} />
              </Container>
            </Grid>
          </>
        )}
      </Grid>
      <CoreGlobalFooter />
    </>
  );
};

export default CaregiverDetail;
